import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authService } from '../api/authService';

export interface UserSelections {
  storeId: string;
  month: number; // 1-12
  year: number; // e.g. 2025
  updatedAt?: string;
}

type SetStateAction<T> = T | ((prev: T) => T);

interface SettingsContextType {
  settings: UserSelections | null;
  setSettings: (next: SetStateAction<UserSelections>) => void;
  username: string | null;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

function makeKey(username: string) {
  return `appOrari:user:${username}:settings`;
}

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [username, setUsername] = useState<string | null>(null);
  const [settings, setSettingsState] = useState<UserSelections | null>(null);

  // Load current user and hydrate settings at mount and when user changes in localStorage
  useEffect(() => {
    const load = () => {
      const user = authService.getCurrentUser();
      const uname = user?.username || null;
      setUsername(uname);
      if (uname) {
        try {
          const raw = localStorage.getItem(makeKey(uname));
          if (raw) {
            const parsed = JSON.parse(raw) as UserSelections;
            setSettingsState(parsed);
          } else {
            setSettingsState(null);
          }
        } catch {
          setSettingsState(null);
        }
      } else {
        setSettingsState(null);
      }
    };

    load();

    // Listen to storage changes for multi-tab sync and userInfo updates
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'userInfo') {
        // user changed (login/logout)
        load();
      } else if (username && e.key === makeKey(username)) {
        if (e.newValue) {
          try { setSettingsState(JSON.parse(e.newValue)); } catch {}
        }
      }
    };
    window.addEventListener('storage', onStorage);
    const onUserChanged = () => load();
    window.addEventListener('apporari-user-changed', onUserChanged as any);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('apporari-user-changed', onUserChanged as any);
    };
  }, [username]);

  const setSettings = useCallback((next: SetStateAction<UserSelections>) => {
    setSettingsState(prev => {
      const value = typeof next === 'function' ? (next as (p: UserSelections | null) => UserSelections)(prev as any) : next;
      const withMeta: UserSelections = { ...value, updatedAt: new Date().toISOString() };
      const user = authService.getCurrentUser();
      const uname = user?.username;
      if (uname) {
        try {
          localStorage.setItem(makeKey(uname), JSON.stringify(withMeta));
        } catch {}
      }
      return withMeta;
    });
  }, []);

  const value = useMemo<SettingsContextType>(() => ({ settings, setSettings, username }), [settings, setSettings, username]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
